import React, { ReactNode } from 'react';

import { Box, Flex, Text, Link, Button } from '@chakra-ui/react';

import { ArrowRightIcon } from '../../../Icons/ArrowRightIcon';
import { ExternalLinkIcon } from '../../../Icons/ExternalLinkIcon';
import { EColor } from '../../../Theme';

interface ProfileMenuLinkProps {
  href?: string;
  title: string;
  body?: string;
  icon: ReactNode;
  notification?: number;
  urgentNotification?: boolean;
  external?: boolean;
  onClick?: () => void;
}

export const ProfileMenuLink = ({
  href,
  title,
  body,
  icon,
  onClick,
  notification,
  urgentNotification,
  external,
}: ProfileMenuLinkProps) => {
  const commonProps = {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    color: EColor.Black,
    textDecoration: 'none',
    _hover: {
      backgroundColor: EColor.Neutral5,
    },
  };

  const content = (
    <>
      <Flex
        alignItems="center"
        backgroundColor={EColor.Neutral50}
        borderRadius="full"
        height="40px"
        justifyContent="center"
        width="40px"
      >
        {icon}
      </Flex>
      <Flex
        alignItems="flex-start"
        flex="1"
        flexDirection="column"
        justifyContent="center"
      >
        <Text fontSize="14px" fontWeight="bold" textTransform="none">
          {title}
        </Text>
        {body && (
          <Text color={EColor.Neutral65} fontSize="12px" textTransform="none">
            {body}
          </Text>
        )}
      </Flex>
      <Box
        alignItems="center"
        display="flex"
        flexShrink={0}
        justifyContent="center"
        position="relative"
        width="32px"
      >
        {!!notification && notification > 0 && (
          <Box
            _groupHover={{ display: 'none' }}
            alignItems="center"
            backgroundColor={urgentNotification ? EColor.Tomato : EColor.Black}
            borderRadius="full"
            color={EColor.White}
            display="flex"
            fontSize="10px"
            fontWeight="bold"
            height="16px"
            justifyContent="center"
            minWidth="16px"
            paddingX="4px"
            paddingY="1px"
            position="absolute"
            right="0"
            top="0"
          >
            {notification}
          </Box>
        )}
        {external && (
          <Box
            _groupHover={{ display: 'none' }}
            display="flex"
            position="absolute"
            right="0"
            top="0"
          >
            <ExternalLinkIcon h="16px" w="16px" />
          </Box>
        )}
        <Box
          _groupHover={{ opacity: '1' }}
          opacity="0"
          position="absolute"
          right="0"
          top="50%"
          transform="translateY(-50%)"
        >
          <ArrowRightIcon h="16px" w="16px" />
        </Box>
      </Box>
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        {...commonProps}
        role="group"
        target={external ? '_blank' : undefined}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <Button
      type="button"
      {...commonProps}
      backgroundColor="transparent"
      border="none"
      borderRadius="0"
      fontWeight="normal"
      height="auto"
      justifyContent="flex-start"
      lineHeight="normal"
      minHeight="auto"
      padding="12px"
      role="group"
      textAlign="left"
      onClick={onClick}
    >
      {content}
    </Button>
  );
};
